import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, HeartIcon, ShieldIcon, BrainIcon, HomeIcon, SparklesIcon, CheckCircle2Icon, XCircleIcon, LockIcon, GiftIcon, FileTextIcon, ZapIcon, CompassIcon, MessageCircleIcon, UsersIcon, TrendingUpIcon, EyeIcon, BookOpenIcon } from 'lucide-react';
const emergencias = [{
  title: 'Aislamiento extremo',
  detail: '(días sin salir del cuarto)',
  meaning: 'Posible depresión o evento traumático no procesado'
}, {
  title: 'Cambios bruscos de conducta',
  detail: 'sin explicación',
  meaning: 'Señal de alerta que requiere evaluación profesional'
}, {
  title: 'Autolesiones',
  detail: '(cortes, golpes, quemaduras)',
  meaning: 'Sufrimiento intenso, nunca manipulación'
}, {
  title: 'Hablar de no querer vivir',
  detail: '',
  meaning: 'Siempre debe tomarse en serio. Sin excepción.'
}, {
  title: 'Ataques de pánico o ansiedad severa',
  detail: '',
  meaning: 'El sistema nervioso está desbordado — no lo controla'
}, {
  title: 'Consumo de sustancias como escape',
  detail: '',
  meaning: 'Busca alivio de un dolor que no sabe nombrar'
}];
const protocoloCalma = [{
  letter: 'C',
  word: 'Contén tu propia emoción',
  detail: 'Respira primero. Si tú estás en pánico, tu hijo lo siente y se cierra más.'
}, {
  letter: 'A',
  word: 'Acércate sin irrumpir',
  detail: 'Golpea antes de entrar. Siéntate cerca, no enfrente.'
}, {
  letter: 'L',
  word: 'Limita juicios y preguntas',
  detail: 'Cero "¿por qué hiciste eso?" en el momento agudo. Solo presencia.'
}, {
  letter: 'M',
  word: 'Mapea el nivel de riesgo',
  detail: 'Pregunta directo: "¿Estás pensando en hacerte daño?" preguntar alivia, no planta la idea.'
}, {
  letter: 'A',
  word: 'Activa los recursos correctos',
  detail: 'Un abrazo, una conversación, o una llamada al médico según lo que detectes.'
}];
const frasesNunca = [{
  bad: '"Eso no es para tanto."',
  good: '"Veo que estás sufriendo mucho. Eso es real."'
}, {
  bad: '"¿Por qué no me dijiste antes?"',
  good: '"Me alegra que me lo estés diciendo ahora."'
}, {
  bad: '"Piensa en todo lo que tienes."',
  good: '"Ahora mismo solo necesito saber cómo te sientes tú."'
}, {
  bad: '"Con lo que me costó criarte..."',
  good: '"Nada de lo que sientes es una carga para mí."'
}];
const incluyeBono1 = ['Qué hacer después de la crisis para fortalecer el vínculo (no lo que la mayoría de padres hace — que sin quererlo empeora todo)', 'Cómo crear un plan de seguridad conjunto con tu hijo antes de que llegue la próxima tormenta', 'El ejercicio de "Tu Plan de Emergencia Personal" para que actúes con calma cuando todo a tu alrededor sea caos'];
const pilares = [{
  icon: HomeIcon,
  name: 'Pertenencia',
  what: 'La certeza de que existe al menos un lugar donde es aceptado sin condiciones',
  how: 'Ese lugar debe ser tu hogar — y depende de ti'
}, {
  icon: TrendingUpIcon,
  name: 'Competencia',
  what: 'La experiencia de poder resolver problemas y superar desafíos',
  how: 'Nace de los retos bien calibrados, no de la sobreprotección'
}, {
  icon: SparklesIcon,
  name: 'Autonomía',
  what: 'La percepción de que sus decisiones tienen peso real',
  how: 'Nace del respeto genuino a su voz dentro de la familia'
}];
const mitos = [{
  myth: '"El amor propio se gana siendo el mejor."',
  truth: 'La autoestima saludable es incondicional — no se gana ni se pierde con los resultados'
}, {
  myth: '"Si le digo que está bien como es, no se esforzará."',
  truth: 'La validación incondicional aumenta la motivación intrínseca, no la reduce'
}, {
  myth: '"Hablar de emociones lo hace más débil."',
  truth: 'Nombrar emociones activa la corteza prefrontal y reduce la intensidad emocional (Lieberman, 2007)'
}, {
  myth: '"La autoestima alta es narcisismo."',
  truth: 'El narcisismo nace de la autoestima frágil — son procesos opuestos'
}];
const amenazas = [{
  icon: EyeIcon,
  title: 'Las redes sociales y la comparación constante',
  body: 'Instagram y TikTok presentan versiones curadas e irreales de la vida de otros. Tu hijo se compara permanentemente con estándares que no existen en la realidad. La investigadora Jean Twenge documentó que el aumento del uso de redes sociales desde 2012 correlaciona directamente con el aumento de ansiedad y depresión adolescente.'
}, {
  icon: ZapIcon,
  title: 'La cultura del rendimiento y el perfeccionismo',
  body: 'El perfeccionismo no es ambición. Es miedo al rechazo disfrazado de excelencia. Y cuando la autoestima depende de los resultados, el primer fracaso puede derrumbarlo todo.'
}, {
  icon: UsersIcon,
  title: 'La soledad emocional en la generación más "conectada"',
  body: 'La generación más conectada tecnológicamente es también la más sola emocionalmente. Las amistades digitales no reemplazan el contacto que el cerebro adolescente necesita para regularse.'
}];
const afirmaciones = ['"No tienes que ser perfecto para merecer mi amor."', '"Veo el esfuerzo que estás poniendo, aunque el resultado no sea el esperado."', '"Tu opinión importa en esta familia."', '"Está bien no estar bien. Puedes decírmelo."', '"No tienes que ganarte un lugar aquí. Ya lo tienes."', '"Equivocarse es parte de aprender. Yo también lo hago."'];
const senalesAlerta = [{
  signal: 'Tristeza o irritabilidad la mayoría de los días',
  when: 'Si dura más de 2 semanas → consulta profesional'
}, {
  signal: 'Caída importante en rendimiento escolar sin causa aparente',
  when: 'Si dura más de 3-4 semanas'
}, {
  signal: 'Cambios marcados en sueño o apetito',
  when: 'Si persiste más de 2 semanas'
}, {
  signal: 'Comentarios negativos frecuentes sobre sí mismo',
  when: 'Desde el primer momento — conversa y evalúa'
}, {
  signal: 'Abandono de actividades que antes disfrutaba',
  when: 'Si se combina con otras señales → actúa'
}];
const incluyeBono2 = ['La Auditoría del Amor Propio en tu Hogar — un ejercicio de reflexión honesta que revela qué ambiente emocional estás creando sin saberlo', 'Cómo hablar de ti mismo delante de tu hijo para modelar el amor propio — porque lo que él te ve hacer pesa mil veces más que lo que le dices', 'Cómo distinguir entre el comportamiento y la persona — la diferencia entre frases que construyen y frases que sin querer destruyen'];
const fadeUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.6
  }
};
export function BonusSection() {
  return <section className="bg-[#FAFAF7] py-24 px-5" data-id="element-25">
      <div className="max-w-5xl mx-auto" data-id="element-26">
        {/* Headline de transición */}
        <motion.div {...fadeUp} className="text-center mb-16" data-id="element-27">
          <div className="inline-flex items-center gap-2 bg-[#F4A535]/15 text-[#B5701A] text-xs font-bold tracking-[3px] uppercase px-4 py-2 rounded-full mb-6" data-id="element-28">
            <GiftIcon className="w-4 h-4" data-id="element-29" />
            Bonos exclusivos incluidos
          </div>
          <h2 className="text-3xl md:text-5xl lg:text-6xl text-[#1A2744] font-bold leading-tight mb-8" data-id="element-30">
            Pero eso no es todo lo que{' '}
            <span className="text-[#F4A535]" data-id="element-31">recibes hoy.</span>
          </h2>
          <div className="max-w-2xl mx-auto space-y-4 text-[#4A5568] text-base md:text-lg leading-relaxed" data-id="element-32">
            <p data-id="element-33">
              Cuando decidimos crear este libro, nos hicimos una pregunta
              incómoda:
            </p>
            <p className="italic text-[#1A2744]" data-id="element-34">
              ¿Qué pasa cuando un padre necesita actuar y no tiene tiempo de
              buscar en Google?
            </p>
            <p className="italic text-[#1A2744]" data-id="element-35">
              ¿Qué pasa cuando tu hijo llega llorando a las 11 de la noche y no
              sabes qué decir?
            </p>
            <p className="italic text-[#1A2744]" data-id="element-36">
              ¿Qué pasa cuando te das cuenta de que él se está apagando por
              dentro... y tú no sabes cómo encenderlo de nuevo?
            </p>
            <p className="pt-2" data-id="element-37">
              Para esos momentos, creamos que normalmente venderíamos por
              separado y que hoy vienen incluidas con tu copia de Adolescencia
              Sin Miedo.
            </p>
          </div>
        </motion.div>

        {/* ============ BONO #1 ============ */}
        <motion.div {...fadeUp} className="bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] border border-[#E2E8F0] overflow-hidden mb-20" data-id="element-38">
          <div className="bg-gradient-to-br from-[#1A2744] to-[#2D3E6B] text-white p-8 md:p-12 grid md:grid-cols-2 gap-10 items-center" data-id="element-39">
            <div data-id="element-40">
              <div className="inline-flex items-center gap-2 bg-[#F4A535] text-[#1A2744] text-xs font-bold tracking-[2px] uppercase px-4 py-2 rounded-full mb-5" data-id="element-41">
                <GiftIcon className="w-4 h-4" data-id="element-42" />
                Bono #1
              </div>
              <h3 className="text-3xl md:text-4xl font-bold leading-tight mb-3" data-id="element-43">
                Emergencias Emocionales en la Adolescencia
              </h3>
              <p className="text-[#B8C8E8] text-base md:text-lg leading-relaxed" data-id="element-44">
                Guía de acción rápida para padres: cómo reconocer, responder y
                acompañar a tu hijo en sus momentos más difíciles.
              </p>
            </div>
            <div className="flex justify-center" data-id="element-45">
              <img src="https://cdn.magicpatterns.com/uploads/s4RzZZMuhsGQojF6N6GZN4/Bono_1.png" alt="Bono 1 — Emergencias Emocionales en la Adolescencia" className="max-w-full h-auto max-h-[360px] drop-shadow-2xl" data-id="element-46" />
            </div>
          </div>

          <div className="p-8 md:p-12" data-id="element-47">
            {/* Datos del bono */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 bg-[#FAFAF7] rounded-2xl p-6 border border-[#E2E8F0]" data-id="element-48">
              <div data-id="element-49">
                <div className="flex items-center gap-2 text-xs font-bold tracking-wide uppercase text-[#718096] mb-1" data-id="element-50">
                  <FileTextIcon className="w-4 h-4 text-[#F4A535]" data-id="element-51" /> Formato
                </div>
                <p className="text-sm font-semibold text-[#1A2744]" data-id="element-52">
                  Guía digital (PDF)
                </p>
              </div>
              <div data-id="element-53">
                <div className="flex items-center gap-2 text-xs font-bold tracking-wide uppercase text-[#718096] mb-1" data-id="element-54">
                  <ZapIcon className="w-4 h-4 text-[#F4A535]" data-id="element-55" /> Acceso
                </div>
                <p className="text-sm font-semibold text-[#1A2744]" data-id="element-56">
                  Inmediato
                </p>
              </div>
              <div data-id="element-57">
                <div className="text-xs font-bold tracking-wide uppercase text-[#718096] mb-1" data-id="element-58">
                  Valor real
                </div>
                <p className="text-sm font-semibold text-[#A0AEC0] line-through" data-id="element-59">
                  $16 USD
                </p>
              </div>
              <div data-id="element-60">
                <div className="text-xs font-bold tracking-wide uppercase text-[#718096] mb-1" data-id="element-61">
                  Tu precio
                </div>
                <p className="text-sm font-extrabold text-[#38A169]" data-id="element-62">GRATIS</p>
              </div>
            </div>

            {/* Por qué existe */}
            <div className="border-l-4 border-[#E53E3E] bg-[#FFF5F5] rounded-r-2xl p-6 mb-10" data-id="element-63">
              <div className="flex items-start gap-3 mb-3" data-id="element-64">
                <AlertTriangleIcon className="w-6 h-6 text-[#E53E3E] flex-shrink-0 mt-0.5" data-id="element-65" />
                <h4 className="text-lg md:text-xl font-bold text-[#1A2744]" data-id="element-66">
                  Este bono existe porque nadie te enseñó qué hacer en el peor
                  momento.
                </h4>
              </div>
              <div className="space-y-3 text-[#4A5568] text-sm md:text-base leading-relaxed pl-9" data-id="element-67">
                <p data-id="element-68">Hay una escena que ningún padre quiere imaginar </p>
                <p data-id="element-69">
                  Tu hijo está encerrado en su cuarto. No sale. No come. No
                  habla.
                </p>
                <p data-id="element-70">O peor: llega con marcas en los brazos.</p>
                <p data-id="element-71">
                  O dice algo que te congela la sangre:{' '}
                  <em data-id="element-72">"No quiero seguir aquí."</em>
                </p>
                <p className="font-semibold text-[#1A2744]" data-id="element-73">
                  En ese momento, el amor no es suficiente. Necesitas saber
                  exactamente qué hacer.
                </p>
                <p data-id="element-74">
                  No lo que sientes que deberías hacer. No lo que harías si
                  pensaras con calma.
                </p>
                <p className="italic" data-id="element-75">
                  Lo que realmente funciona, en ese preciso segundo, cuando el
                  pánico ya está en tu pecho.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-6" data-id="element-76">
              <CheckCircle2Icon className="w-6 h-6 text-[#38A169]" data-id="element-77" />
              <h4 className="text-xl md:text-2xl font-bold text-[#1A2744]" data-id="element-78">
                Esto es lo que encontrarás dentro:
              </h4>
            </div>

            {/* Las 6 emergencias */}
            <div className="mb-12" data-id="element-79">
              <div className="flex items-center gap-2 mb-5" data-id="element-80">
                <AlertTriangleIcon className="w-5 h-5 text-[#E53E3E]" data-id="element-81" />
                <h5 className="text-base md:text-lg font-bold text-[#1A2744] uppercase tracking-wide" data-id="element-82">
                  Las 6 emergencias más frecuentes
                </h5>
              </div>
              <div className="grid md:grid-cols-2 gap-4" data-id="element-83">
                {emergencias.map((e, i) => <div key={i} className="bg-white border border-[#E2E8F0] rounded-xl p-5 hover:border-[#E53E3E]/40 transition-colors" data-id="element-84">
                    <p className="font-bold text-[#1A2744] text-sm mb-1" data-id="element-85">
                      {e.title}{' '}
                      {e.detail && <span className="font-normal text-[#718096]" data-id="element-86">
                          {e.detail}
                        </span>}
                    </p>
                    <p className="text-sm text-[#4A5568]" data-id="element-87">{e.meaning}</p>
                  </div>)}
              </div>
            </div>

            {/* Protocolo CALMA */}
            <div className="mb-12 bg-gradient-to-br from-[#F0F4F0] to-[#FAFAF7] rounded-2xl p-6 md:p-8 border border-[#38A169]/20" data-id="element-88">
              <div className="flex items-start gap-3 mb-5" data-id="element-89">
                <CompassIcon className="w-6 h-6 text-[#38A169] flex-shrink-0 mt-1" data-id="element-90" />
                <div data-id="element-91">
                  <h5 className="text-lg md:text-xl font-bold text-[#1A2744] mb-1" data-id="element-92">
                    El Protocolo CALMA
                  </h5>
                  <p className="text-sm text-[#4A5568]" data-id="element-93">
                    5 pasos para actuar con cabeza fría cuando el corazón está
                    en pánico. Basado en los Primeros Auxilios Psicológicos de
                    la OMS adaptado para padres sin formación clínica.
                  </p>
                </div>
              </div>
              <div className="space-y-3" data-id="element-94">
                {protocoloCalma.map((step, i) => <div key={i} className="flex items-start gap-4 bg-white rounded-xl p-4 border border-[#E2E8F0]" data-id="element-95">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#38A169] text-white text-xl font-black flex items-center justify-center" data-id="element-96">
                      {step.letter}
                    </div>
                    <div data-id="element-97">
                      <p className="font-bold text-[#1A2744] text-sm md:text-base" data-id="element-98">
                        {step.word}
                      </p>
                      <p className="text-sm text-[#4A5568] mt-1" data-id="element-99">
                        {step.detail}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            <blockquote className="border-l-4 border-[#F4A535] pl-5 py-2 italic text-[#1A2744] text-base md:text-lg" data-id="element-100">
              "No puedes evitar todas las tormentas que vivirá tu hijo. Pero sí
              puedes ser el refugio al que siempre puede regresar."
            </blockquote>
          </div>
        </motion.div>

        {/* ============ BONO #2 ============ */}
        <motion.div {...fadeUp} className="bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] border border-[#E2E8F0] overflow-hidden mb-16" data-id="element-101">
          <div className="bg-gradient-to-br from-[#7C5C6F] to-[#A57C8E] text-white p-8 md:p-12 grid md:grid-cols-2 gap-10 items-center" data-id="element-102">
            <div data-id="element-103">
              <div className="inline-flex items-center gap-2 bg-[#F4A535] text-[#1A2744] text-xs font-bold tracking-[2px] uppercase px-4 py-2 rounded-full mb-5" data-id="element-104">
                <GiftIcon className="w-4 h-4" data-id="element-105" />
                Bono #2
              </div>
              <h3 className="text-3xl md:text-4xl font-bold leading-tight mb-3" data-id="element-106">
                Amor Propio y Salud Mental en la Adolescencia
              </h3>
              <p className="text-white/80 text-base md:text-lg leading-relaxed" data-id="element-107">
                Cómo cultivar la autoestima saludable de tu hijo, proteger su
                bienestar emocional y modelar el amor propio desde casa.
              </p>
            </div>
            <div className="flex justify-center" data-id="element-108">
              <img src="https://cdn.magicpatterns.com/uploads/pfraPYWpm5jadMB8XQtPVa/Bono_2.png" alt="Bono 2 — Amor Propio y Salud Mental en la Adolescencia" className="max-w-full h-auto max-h-[360px] drop-shadow-2xl" data-id="element-109" />
            </div>
          </div>

          <div className="p-8 md:p-12" data-id="element-110">
            {/* Datos del bono */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 bg-[#FAFAF7] rounded-2xl p-6 border border-[#E2E8F0]" data-id="element-111">
              <div data-id="element-112">
                <div className="flex items-center gap-2 text-xs font-bold tracking-wide uppercase text-[#718096] mb-1" data-id="element-113">
                  <FileTextIcon className="w-4 h-4 text-[#F4A535]" data-id="element-114" /> Formato
                </div>
                <p className="text-sm font-semibold text-[#1A2744]" data-id="element-115">
                  Guía digital (PDF)
                </p>
              </div>
              <div data-id="element-116">
                <div className="flex items-center gap-2 text-xs font-bold tracking-wide uppercase text-[#718096] mb-1" data-id="element-117">
                  <ZapIcon className="w-4 h-4 text-[#F4A535]" data-id="element-118" /> Acceso
                </div>
                <p className="text-sm font-semibold text-[#1A2744]" data-id="element-119">
                  Inmediato
                </p>
              </div>
              <div data-id="element-120">
                <div className="text-xs font-bold tracking-wide uppercase text-[#718096] mb-1" data-id="element-121">
                  Valor real
                </div>
                <p className="text-sm font-semibold text-[#A0AEC0] line-through" data-id="element-122">
                  $16 USD
                </p>
              </div>
              <div data-id="element-123">
                <div className="text-xs font-bold tracking-wide uppercase text-[#718096] mb-1" data-id="element-124">
                  Tu precio
                </div>
                <p className="text-sm font-extrabold text-[#38A169]" data-id="element-125">GRATIS</p>
              </div>
            </div>

            {/* Por qué existe */}
            <div className="border-l-4 border-[#7C5C6F] bg-[#FAF5F7] rounded-r-2xl p-6 mb-10" data-id="element-126">
              <div className="flex items-start gap-3 mb-3" data-id="element-127">
                <HeartIcon className="w-6 h-6 text-[#7C5C6F] flex-shrink-0 mt-0.5" data-id="element-128" />
                <h4 className="text-lg md:text-xl font-bold text-[#1A2744]" data-id="element-129">
                  Este bono existe porque la crisis más silenciosa no llega con
                  alarmas.
                </h4>
              </div>
              <div className="space-y-3 text-[#4A5568] text-sm md:text-base leading-relaxed pl-9" data-id="element-130">
                <p data-id="element-131">
                  No tiene que llegar una emergencia para que tu hijo esté
                  sufriendo.
                </p>
                <p data-id="element-132">A veces es más sutil que eso.</p>
                <p data-id="element-133">
                  Es el comentario que hace sobre su cuerpo cuando se mira al
                  espejo.
                </p>
                <p data-id="element-134">
                  Es la forma en que borra sus propias opiniones antes de que
                  alguien más pueda rechazarlas.
                </p>
                <p data-id="element-135">
                  Es el adolescente que trabaja sin parar para demostrar que
                  vale porque en algún lugar, en algún momento, aprendió que su
                  valor se gana.
                </p>
                <p className="font-semibold text-[#1A2744]" data-id="element-136">
                  El amor propio no se improvisa en la adolescencia. Se
                  construye. Ladrillo a ladrillo. Conversación a conversación.
                </p>
                <p data-id="element-137">
                  Y la herramienta más poderosa que tienes para construirlo no
                  es lo que le dices a tu hijo.
                </p>
                <p className="italic font-semibold text-[#7C5C6F]" data-id="element-138">
                  Es lo que él te ve hacer a ti.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-6" data-id="element-139">
              <CheckCircle2Icon className="w-6 h-6 text-[#38A169]" data-id="element-140" />
              <h4 className="text-xl md:text-2xl font-bold text-[#1A2744]" data-id="element-141">
                Esto es lo que encontrarás dentro:
              </h4>
            </div>

            {/* Los 3 pilares */}
            <div className="mb-12" data-id="element-142">
              <div className="flex items-center gap-2 mb-2" data-id="element-143">
                <ShieldIcon className="w-5 h-5 text-[#7C5C6F]" data-id="element-144" />
                <h5 className="text-base md:text-lg font-bold text-[#1A2744] uppercase tracking-wide" data-id="element-145">
                  Los 3 Pilares del Amor Propio Adolescente
                </h5>
              </div>
              <p className="text-sm text-[#718096] mb-5 italic" data-id="element-146">
                Cuando estos tres están presentes, actúan como escudo ante el
                fracaso, el rechazo y la presión social.
              </p>
              <div className="grid md:grid-cols-3 gap-5" data-id="element-147">
                {pilares.map((p, i) => {
                const Icon = p.icon;
                return <div key={i} className="bg-gradient-to-br from-[#FAF5F7] to-white border border-[#E2E8F0] rounded-2xl p-6" data-id="element-148">
                      <div className="w-12 h-12 rounded-xl bg-[#7C5C6F]/10 flex items-center justify-center mb-4" data-id="element-149">
                        <Icon className="w-6 h-6 text-[#7C5C6F]" data-id="element-150" />
                      </div>
                      <h6 className="font-bold text-[#1A2744] text-base mb-2" data-id="element-151">
                        {p.name}
                      </h6>
                      <p className="text-sm text-[#4A5568] mb-3" data-id="element-152">{p.what}</p>
                      <p className="text-xs text-[#7C5C6F] font-semibold border-t border-[#E2E8F0] pt-3" data-id="element-153">
                        {p.how}
                      </p>
                    </div>;
              })}
              </div>
            </div>

            {/* Las 3 amenazas */}
            <div className="mb-12" data-id="element-154">
              <div className="flex items-center gap-2 mb-5" data-id="element-155">
                <AlertTriangleIcon className="w-5 h-5 text-[#F4A535]" data-id="element-156" />
                <h5 className="text-base md:text-lg font-bold text-[#1A2744] uppercase tracking-wide" data-id="element-157">
                  Las 3 amenazas silenciosas que atacan su autoestima ahora
                  mismo
                </h5>
              </div>
              <div className="space-y-4" data-id="element-158">
                {amenazas.map((a, i) => {
                const Icon = a.icon;
                return <div key={i} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 flex gap-4" data-id="element-159">
                      <div className="flex-shrink-0" data-id="element-160">
                        <div className="w-12 h-12 rounded-xl bg-[#F4A535]/15 flex items-center justify-center" data-id="element-161">
                          <Icon className="w-6 h-6 text-[#B5701A]" data-id="element-162" />
                        </div>
                        <div className="text-center mt-2 text-2xl font-black text-[#F4A535]" data-id="element-163">
                          {i + 1}
                        </div>
                      </div>
                      <div data-id="element-164">
                        <h6 className="font-bold text-[#1A2744] text-base md:text-lg mb-2" data-id="element-165">
                          {a.title}
                        </h6>
                        <p className="text-sm md:text-base text-[#4A5568] leading-relaxed" data-id="element-166">
                          {a.body}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
            </div>

            <blockquote className="border-l-4 border-[#7C5C6F] pl-5 py-2 italic text-[#1A2744] text-base md:text-lg" data-id="element-167">
              "La manera en que hablamos a nuestros hijos se convierte en su voz
              interior."
            </blockquote>
          </div>
        </motion.div>

        {/* ============ RESUMEN DEL VALOR ============ */}
        <motion.div {...fadeUp} className="bg-gradient-to-br from-[#1A2744] to-[#2D3E6B] text-white rounded-3xl p-6 sm:p-8 md:p-12 mb-20 shadow-[0_20px_60px_rgba(0,0,0,0.15)]" data-id="element-168">
          <div className="text-center mb-8" data-id="element-169">
            <div className="inline-flex items-center gap-2 bg-[#F4A535] text-[#1A2744] text-[10px] sm:text-xs font-bold tracking-[2px] uppercase px-4 py-2 rounded-full mb-4" data-id="element-170">
              <SparklesIcon className="w-4 h-4" data-id="element-171" />
              Resumen total del valor
            </div>
            <h3 className="text-2xl md:text-4xl font-bold leading-tight" data-id="element-172">
              Lo que recibes hoy
            </h3>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-id="element-173">
            {/* Header — solo desktop */}
            <div className="hidden md:grid grid-cols-[1fr_auto_auto] gap-6 px-8 py-4 bg-white/5 text-xs font-bold uppercase tracking-wide text-[#B8C8E8]" data-id="element-174">
              <div data-id="element-175">Qué recibes</div>
              <div className="text-right w-28" data-id="element-176">Valor real</div>
              <div className="text-right w-28" data-id="element-177">Tu precio</div>
            </div>

            {/* Row 1 */}
            <div className="px-5 md:px-8 py-5 border-t border-white/10 md:grid md:grid-cols-[1fr_auto_auto] md:gap-6 md:items-center" data-id="element-178">
              <div className="flex items-center gap-3 mb-3 md:mb-0" data-id="element-179">
                <BookOpenIcon className="w-5 h-5 text-[#F4A535] flex-shrink-0" data-id="element-180" />
                <span className="text-sm md:text-base font-semibold" data-id="element-181">
                  Adolescencia Sin Miedo (libro principal)
                </span>
              </div>
              <div className="flex items-center justify-between md:contents" data-id="element-182">
                <span className="text-[10px] font-bold uppercase tracking-wide text-[#B8C8E8] md:hidden" data-id="element-183">
                  Valor real
                </span>
                <div className="text-right md:w-28 text-sm text-[#B8C8E8]" data-id="element-184">
                  $19.99
                </div>
              </div>
              <div className="flex items-center justify-between mt-1 md:mt-0 md:contents" data-id="element-185">
                <span className="text-[10px] font-bold uppercase tracking-wide text-[#B8C8E8] md:hidden" data-id="element-186">
                  Tu precio
                </span>
                <div className="text-right md:w-28 text-sm font-bold text-white" data-id="element-187">
                  $7.99
                </div>
              </div>
            </div>

            {/* Row 2 */}
            <div className="px-5 md:px-8 py-5 border-t border-white/10 md:grid md:grid-cols-[1fr_auto_auto] md:gap-6 md:items-center" data-id="element-188">
              <div className="flex items-center gap-3 mb-3 md:mb-0" data-id="element-189">
                <GiftIcon className="w-5 h-5 text-[#F4A535] flex-shrink-0" data-id="element-190" />
                <span className="text-sm md:text-base" data-id="element-191">
                  <strong data-id="element-192">Bono #1</strong> — Emergencias Emocionales
                </span>
              </div>
              <div className="flex items-center justify-between md:contents" data-id="element-193">
                <span className="text-[10px] font-bold uppercase tracking-wide text-[#B8C8E8] md:hidden" data-id="element-194">
                  Valor real
                </span>
                <div className="text-right md:w-28 text-sm text-[#B8C8E8] line-through" data-id="element-195">
                  $16
                </div>
              </div>
              <div className="flex items-center justify-between mt-1 md:mt-0 md:contents" data-id="element-196">
                <span className="text-[10px] font-bold uppercase tracking-wide text-[#B8C8E8] md:hidden" data-id="element-197">
                  Tu precio
                </span>
                <div className="text-right md:w-28 text-sm font-extrabold text-[#38A169]" data-id="element-198">
                  GRATIS
                </div>
              </div>
            </div>

            {/* Row 3 */}
            <div className="px-5 md:px-8 py-5 border-t border-white/10 md:grid md:grid-cols-[1fr_auto_auto] md:gap-6 md:items-center" data-id="element-199">
              <div className="flex items-center gap-3 mb-3 md:mb-0" data-id="element-200">
                <GiftIcon className="w-5 h-5 text-[#F4A535] flex-shrink-0" data-id="element-201" />
                <span className="text-sm md:text-base" data-id="element-202">
                  <strong data-id="element-203">Bono #2</strong> — Amor Propio y Salud Mental
                </span>
              </div>
              <div className="flex items-center justify-between md:contents" data-id="element-204">
                <span className="text-[10px] font-bold uppercase tracking-wide text-[#B8C8E8] md:hidden" data-id="element-205">
                  Valor real
                </span>
                <div className="text-right md:w-28 text-sm text-[#B8C8E8] line-through" data-id="element-206">
                  $16
                </div>
              </div>
              <div className="flex items-center justify-between mt-1 md:mt-0 md:contents" data-id="element-207">
                <span className="text-[10px] font-bold uppercase tracking-wide text-[#B8C8E8] md:hidden" data-id="element-208">
                  Tu precio
                </span>
                <div className="text-right md:w-28 text-sm font-extrabold text-[#38A169]" data-id="element-209">
                  GRATIS
                </div>
              </div>
            </div>

            {/* Total */}
            <div className="px-5 md:px-8 py-5 border-t border-white/20 bg-[#F4A535]/10 md:grid md:grid-cols-[1fr_auto_auto] md:gap-6 md:items-center" data-id="element-210">
              <div className="text-sm md:text-base font-bold mb-3 md:mb-0" data-id="element-211">
                Valor total de los bonos
              </div>
              <div className="flex items-center justify-between md:contents" data-id="element-212">
                <span className="text-[10px] font-bold uppercase tracking-wide text-[#B8C8E8] md:hidden" data-id="element-213">
                  Valor real
                </span>
                <div className="text-right md:w-28 text-sm font-semibold text-[#B8C8E8] line-through" data-id="element-214">
                  $32
                </div>
              </div>
              <div className="flex items-center justify-between mt-1 md:mt-0 md:contents" data-id="element-215">
                <span className="text-[10px] font-bold uppercase tracking-wide text-[#B8C8E8] md:hidden" data-id="element-216">
                  Tu precio
                </span>
                <div className="text-right md:w-28 text-base font-extrabold text-[#F4A535]" data-id="element-217">
                  $0
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-sm sm:text-base md:text-lg text-[#F4A535] font-bold mt-6 flex items-center justify-center gap-2 px-2" data-id="element-218">
            <ZapIcon className="w-5 h-5 flex-shrink-0" data-id="element-219" />
            <span data-id="element-220">Estos dos bonos los llevas gratis. Solo por actuar hoy.</span>
          </p>
        </motion.div>

        {/* ============ CIERRE EMOCIONAL ============ */}
        <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center" data-id="element-221">
          <div className="space-y-5 text-[#4A5568] text-base md:text-lg leading-relaxed" data-id="element-222">
            <p className="text-xl md:text-2xl text-[#1A2744] font-bold" data-id="element-223">
              Ser padre de un adolescente es uno de los trabajos más difíciles
              del mundo.
            </p>
            <p data-id="element-224">No porque tus hijos sean difíciles.</p>
            <p data-id="element-225">
              Sino porque{' '}
              <strong className="text-[#1A2744]" data-id="element-226">
                nadie te entrenó para esto.
              </strong>
            </p>
            <p data-id="element-227">Nadie te enseñó qué hacer cuando tu hijo se cierra.</p>
            <p data-id="element-228">Cuando se hace daño.</p>
            <p data-id="element-229">Cuando deja de creer en sí mismo.</p>
            <p data-id="element-230">Cuando el mundo parece más grande que él.</p>
            <p className="text-lg md:text-xl text-[#1A2744] font-semibold pt-2" data-id="element-231">
              Hoy tienes la oportunidad de cambiar eso.
            </p>
            <p data-id="element-232">No con más miedo. No con más control.</p>
            <p data-id="element-233">
              Con información real, herramientas concretas y el vínculo más
              poderoso que existe:{' '}
              <strong className="text-[#F4A535]" data-id="element-234">el tuyo.</strong>
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}